import { ref, watch, onBeforeUnmount } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

// The sidebar has two Tailwind defaults depending on breakpoint:
//   md:w-[320px]  /  2xl:w-[360px]
// MAX_WIDTH must cover the larger of the two so a drag on 2xl screens
// doesn't immediately truncate to 320.
const DEFAULT_WIDTH_MD = 320;
const DEFAULT_WIDTH_2XL = 360;
const MAX_WIDTH = DEFAULT_WIDTH_2XL;

// Allow shrinking up to 15% below the smallest default (320):
//   floor(320 * 0.85) = 272
const MIN_WIDTH = Math.floor(DEFAULT_WIDTH_MD * 0.85);

// Tailwind 2xl breakpoint
const BREAKPOINT_2XL = 1536;

/**
 * Clamp a numeric value between MIN_WIDTH and MAX_WIDTH.
 * Returns null for non-finite values (guards against NaN / corrupted data).
 */
function clampWidth(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, num));
}

/**
 * Resolve the Tailwind default width for the current viewport.
 * ≥1536px (2xl) → 360px; otherwise → 320px.
 */
function resolveDefaultWidth() {
  return window.innerWidth >= BREAKPOINT_2XL
    ? DEFAULT_WIDTH_2XL
    : DEFAULT_WIDTH_MD;
}

/**
 * Composable for resizing the contact sidebar (right panel).
 *
 * The handle sits on the LEFT edge of the sidebar. Dragging RIGHT shrinks
 * the panel; dragging LEFT grows it (capped at MAX_WIDTH = 360).
 * Persisted under `contact_sidebar_width` in uiSettings.
 */
export function useContactSidebarResize() {
  const { uiSettings, updateUISettings } = useUISettings();

  const savedRaw = uiSettings.value?.contact_sidebar_width;
  const clamped = clampWidth(savedRaw);

  // null = user never resized → sidebar uses its Tailwind defaults
  const sidebarWidth = ref(clamped);
  const isDragging = ref(false);

  let startX = 0;
  let startWidth = 0;

  function onDrag(event) {
    if (!isDragging.value) return;

    // Positive delta (mouse moves RIGHT) shrinks the sidebar;
    // Negative delta (mouse moves LEFT) grows it (capped at MAX_WIDTH).
    const delta = event.clientX - startX;
    const newWidth = clampWidth(startWidth - delta);
    if (newWidth !== null) {
      sidebarWidth.value = newWidth;
    }
  }

  function stopDrag() {
    if (!isDragging.value) return;
    isDragging.value = false;
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', stopDrag);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Persist to UI settings
    if (sidebarWidth.value !== null) {
      updateUISettings({ contact_sidebar_width: sidebarWidth.value });
    }
  }

  function startDrag(event) {
    if (isDragging.value) return;

    event.preventDefault();
    isDragging.value = true;
    startX = event.clientX;

    // Use persisted width if available. Otherwise, resolve the CSS default
    // from the current viewport (320 on md, 360 on 2xl) to avoid an immediate
    // jump when the user first starts dragging on wide screens.
    startWidth = sidebarWidth.value ?? resolveDefaultWidth();

    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', stopDrag);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  function resetWidth() {
    sidebarWidth.value = null;
    updateUISettings({ contact_sidebar_width: null });
  }

  // Sync if settings change externally (e.g., another tab)
  watch(
    () => uiSettings.value?.contact_sidebar_width,
    newVal => {
      if (isDragging.value) return;
      if (newVal == null) {
        sidebarWidth.value = null;
        return;
      }
      const val = clampWidth(newVal);
      if (val !== null) {
        sidebarWidth.value = val;
      }
    }
  );

  onBeforeUnmount(() => {
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', stopDrag);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  });

  return {
    sidebarWidth,
    isDragging,
    startDrag,
    resetWidth,
    DEFAULT_WIDTH: DEFAULT_WIDTH_MD,
    MIN_WIDTH,
    MAX_WIDTH,
  };
}
